<template>
    <div class="variance-graph" ref="graphRef">
        <canvas ref="imageCanvasRef" class="image-canvas"></canvas>
        <!-- 배경 -->
        <canvas ref="pointCanvasRef" class="point-canvas"></canvas>
        <!-- 포인트들 -->
        <canvas ref="waveCanvasRef" class="wave-canvas"></canvas>
        <!-- 파동 -->
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, type Ref, onUnmounted } from 'vue';
import type { VarianceVarianceResDto } from '@common/openapi-client/openapi';
import { BODYCOMP_INFO_LIST } from '@common/common/const/inbodyData';

const props = defineProps<{
    data: VarianceVarianceResDto;
    screenWidth: number;
    shouldAnimate: boolean;
    isForShare?: boolean;
}>();

const LABEL_TOTAL_HEIGHT = 19; // 라벨 영역 높이 (상단 여백 포함)
const BOTTOM_LABEL_HEIGHT = 17; // 👈 아래쪽 레이블 공간
const POINT_RADIUS = 0.01;
const USER_LINE_WIDTH = 2.5;
const NORMAL_LINE_WIDTH = 1;

const graphRef = ref<HTMLDivElement | null>(null);
const imageCanvasRef = ref<HTMLCanvasElement | null>(null);
const pointCanvasRef = ref<HTMLCanvasElement | null>(null);
const waveCanvasRef = ref<HTMLCanvasElement | null>(null);

const imageCtx = ref<CanvasRenderingContext2D | null>(null);
const pointCtx = ref<CanvasRenderingContext2D | null>(null);
const waveCtx = ref<CanvasRenderingContext2D | null>(null);

const waveAnimationFrameId = ref<number | null>(null);

const animatedPoints = ref<
    {
        x: number;
        y: number;
        targetX: number;
        targetY: number;
        value: number | undefined;
        delay: number;
        trailAlpha: number;
        isUserPoint: boolean;
    }[]
>([]);

const originRatio = 141 / 360;

// isForShare이고 screenWidth가 560 초과일 때 너비 제한
const effectiveWidth = computed(() => {
    if (props.isForShare && props.screenWidth > 560) {
        return 560;
    }
    return props.screenWidth;
});

const width = ref(window.innerWidth);
const height = ref(width.value * originRatio);
// const offscreenCanvases = ref<{ [key: string]: HTMLCanvasElement }>({});
// const isIncreaseGraph = ref(false);

const isAnimated = ref(false);
watch(
    () => props.shouldAnimate,
    newVal => {
        if (newVal && !isAnimated.value) {
            animateGraphPoints();
            // 포인트 확산 애니메이션
            // startAnimationForUserPoint();
            isAnimated.value = true;
        }
    },
);

// Decrease인 경우 모든 값이 반전
const isDecrease = computed(() => props.data.GraphDirection === 'Decrease');
// const isDecrease = computed(() => false);

// 반전 처리
const filteredNormalized = computed(() =>
    isDecrease.value
        ? [...(props.data.FieldInfo?.NormalizedValueList ?? [])].reverse()
        : (props.data.FieldInfo?.NormalizedValueList ?? []),
);

const filteredNameList = computed(() =>
    isDecrease.value ? [...(props.data.FieldInfo?.NameList ?? [])].reverse() : (props.data.FieldInfo?.NameList ?? []),
);

const filteredDisplayValueList = computed(() =>
    isDecrease.value
        ? [...(props.data.FieldInfo?.DisplayValueList ?? [])].reverse()
        : (props.data.FieldInfo?.DisplayValueList ?? []),
);

const filteredFieldColorList = computed(() =>
    isDecrease.value
        ? [...(props.data.FieldInfo?.FieldColorList ?? [])].reverse()
        : (props.data.FieldInfo?.FieldColorList ?? []),
);

const computedUnit = computed(() => {
    const info = BODYCOMP_INFO_LIST.find(item => item.id === props.data.BodyComp);

    if (!info) return '';

    const unit = info.unit;
    return unit;
});

const setSize = (): void => {
    const dpr = 3;
    const canvasWidth = effectiveWidth.value;
    const canvasHeight = canvasWidth * originRatio + LABEL_TOTAL_HEIGHT + BOTTOM_LABEL_HEIGHT;

    width.value = canvasWidth;
    height.value = canvasHeight;

    const setupCanvas = (canvas: HTMLCanvasElement | null, ctxRef: Ref<CanvasRenderingContext2D | null>) => {
        if (!canvas) return;
        canvas.width = canvasWidth * dpr;
        canvas.height = canvasHeight * dpr;
        canvas.style.width = `${canvasWidth}px`;
        canvas.style.height = `${canvasHeight}px`;

        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (ctx) {
            ctx.setTransform(1, 0, 0, 1, 0, 0); // 초기화
            ctx.scale(dpr, dpr); // ✅ 3배 확대 적용
        }
        ctxRef.value = ctx;
    };

    setupCanvas(imageCanvasRef.value, imageCtx);
    setupCanvas(pointCanvasRef.value, pointCtx);
    setupCanvas(waveCanvasRef.value, waveCtx);

    graphRef.value!.style.width = `${canvasWidth}px`;
    graphRef.value!.style.height = `${canvasHeight}px`;
};

const drawElem = async (): Promise<void> => {
    if (!imageCanvasRef.value || !imageCtx.value) return;

    const ctx = imageCtx.value;
    const canvas = imageCanvasRef.value;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    drawColorGradientBackground();
    drawDashedBoundaries();
    drawBlockLabels();
    drawBottomLabels();
};

const drawFullRoundedClip = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    ctx.clip();
};

const drawColorGradientBackground = () => {
    if (!imageCanvasRef.value || !imageCtx.value) return;

    const ctx = imageCtx.value;
    const canvas = imageCanvasRef.value;

    const totalW = effectiveWidth.value;
    const totalH = effectiveWidth.value * originRatio;
    const offsetY = LABEL_TOTAL_HEIGHT;
    const blendPx = totalW * 0.05;
    const contentHeight = totalH;

    const colorList = filteredFieldColorList.value;
    const normalized = isDecrease.value ? [...filteredNormalized.value].reverse() : filteredNormalized.value;

    if (normalized.length !== 4) return;

    // ✅ 1. 각 블록의 너비 비율 계산 (절대값 사용)
    const ratios = [
        Math.abs(normalized[1] - normalized[0]),
        Math.abs(normalized[2] - normalized[1]),
        Math.abs(normalized[3] - normalized[2]),
    ];

    // ✅ 2. 누적 위치 비율 (0 ~ 1)
    const accRatios = isDecrease.value
        ? [0, ratios[2], ratios[2] + ratios[1], 1] // 오른쪽에서 왼쪽으로
        : [0, ratios[0], ratios[0] + ratios[1], 1]; // 왼쪽에서 오른쪽으로

    // ✅ 3. 실제 좌표(px)
    const sectionXs = accRatios.map(v => v * totalW);

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    drawFullRoundedClip(ctx, 0, offsetY, totalW, contentHeight, 5);

    for (let i = 0; i < 3; i++) {
        const rawStart = sectionXs[i];
        const rawEnd = sectionXs[i + 1];
        const isFirst = i === 0;
        const isLast = i === 2;

        const gradStart = isFirst ? rawStart : rawStart + blendPx;
        const gradEnd = isLast ? rawEnd : rawEnd - blendPx;

        const x1 = Math.min(gradStart, gradEnd);
        const x2 = Math.max(gradStart, gradEnd);

        const gradient = ctx.createLinearGradient(x1, 0, x2, 0);
        gradient.addColorStop(0, colorList[i * 2]);
        gradient.addColorStop(1, colorList[i * 2 + 1]);

        ctx.fillStyle = gradient;
        ctx.fillRect(x1, offsetY, x2 - x1, contentHeight);
    }

    // ✅ 4. Blend 영역도 누적 비율 기준으로 재계산
    const blendCenters = isDecrease.value ? [ratios[2], ratios[2] + ratios[1]] : [ratios[0], ratios[0] + ratios[1]];

    blendCenters.forEach((relX, i) => {
        const centerX = relX * totalW;
        const blendStart = centerX - blendPx;
        const blendEnd = centerX + blendPx;

        const fromColor = colorList[i * 2 + 1];
        const toColor = colorList[(i + 1) * 2];

        const gradient = ctx.createLinearGradient(blendStart, 0, blendEnd, 0);
        gradient.addColorStop(0, fromColor);
        gradient.addColorStop(1, toColor);

        ctx.fillStyle = gradient;
        ctx.fillRect(blendStart, offsetY, blendEnd - blendStart, contentHeight);
    });

    ctx.restore();
};

const drawDashedBoundaries = () => {
    if (!imageCanvasRef.value || !imageCtx.value) return;

    const ctx = imageCtx.value;
    const totalW = effectiveWidth.value;
    const contentHeight = totalW * originRatio;

    const normalized = isDecrease.value ? [...filteredNormalized.value].reverse() : filteredNormalized.value;
    if (normalized.length < 4) return;

    const sectionX1 = isDecrease.value ? totalW * (1 - normalized[1]) : totalW * normalized[1];
    const sectionX2 = isDecrease.value ? totalW * (1 - normalized[2]) : totalW * normalized[2];

    const yStart = LABEL_TOTAL_HEIGHT; // ✅ 라벨 영역 포함해서 최상단부터
    const yEnd = LABEL_TOTAL_HEIGHT + contentHeight;

    ctx.save();
    ctx.setLineDash([4, 2]);
    ctx.lineWidth = 1;
    ctx.strokeStyle =
        props.data.FieldInfo?.LineColor ??
        (isDecrease.value ? filteredFieldColorList.value[0] : filteredFieldColorList.value[5]);

    [sectionX1, sectionX2].forEach(x => {
        ctx.beginPath();
        ctx.moveTo(x + 0.5, yStart); // ✅ anti-aliasing 보정 포함
        ctx.lineTo(x + 0.5, yEnd);
        ctx.stroke();
    });

    ctx.restore();
};

const drawBlockLabels = () => {
    if (!imageCanvasRef.value || !imageCtx.value) return;

    const ctx = imageCtx.value;
    const totalW = effectiveWidth.value;

    const rawNormalized = filteredNormalized.value;
    if (rawNormalized.length !== 4) return;

    // 1. 정방향/역방향 맞추기
    const normalized = isDecrease.value ? [...rawNormalized].reverse() : rawNormalized;

    // 2. 각 구간 비율 계산 (0~1 범위)
    const ratios = isDecrease.value
        ? [normalized[3] - normalized[2], normalized[2] - normalized[1], normalized[1] - normalized[0]]
        : [normalized[1] - normalized[0], normalized[2] - normalized[1], normalized[3] - normalized[2]];

    // 3. 누적 시작 좌표 계산
    const sectionStarts: number[] = [];
    let acc = 0;
    for (const r of ratios) {
        sectionStarts.push(acc);
        acc += r;
    }

    // 4. 실제 픽셀 기준 좌표
    const sectionWidths = ratios.map(r => r * totalW);
    const sectionStartXs = sectionStarts.map(r => r * totalW);

    const textColors = isDecrease.value
        ? [filteredFieldColorList.value[0], filteredFieldColorList.value[1], '#B3BBC5']
        : ['#B3BBC5', filteredFieldColorList.value[4], filteredFieldColorList.value[5]];
    const nameList = filteredNameList.value;

    ctx.save();
    ctx.font = 'bold 12px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const labelY = LABEL_TOTAL_HEIGHT / 2;

    sectionStartXs.forEach((startX, i) => {
        const width = sectionWidths[i];
        const centerX = startX + width / 2;

        const labelText = nameList[i] ?? `Label ${i + 1}`;
        ctx.fillStyle = textColors[i];
        ctx.fillText(labelText, centerX, labelY);
    });

    ctx.restore();
};

const drawBottomLabels = () => {
    if (!imageCanvasRef.value || !imageCtx.value) return;

    const ctx = imageCtx.value;
    const totalW = effectiveWidth.value;
    const contentHeight = totalW * originRatio;
    // const canvasHeight =
    contentHeight + LABEL_TOTAL_HEIGHT + BOTTOM_LABEL_HEIGHT;

    const labelList = filteredDisplayValueList.value;
    if (labelList.length !== 4) return;

    const rawNormalized = filteredNormalized.value;
    if (rawNormalized.length !== 4) return;

    // 1. 정방향/역방향 맞추기
    const normalized = isDecrease.value ? [...rawNormalized].reverse() : rawNormalized;

    // 2. 각 구간 비율 계산 (0~1 범위)
    const ratio = isDecrease.value
        ? [normalized[3] - normalized[2], normalized[2] - normalized[1], normalized[1] - normalized[0]]
        : [normalized[1] - normalized[0], normalized[2] - normalized[1], normalized[3] - normalized[2]];

    const ratioSum = ratio.reduce((a, b) => a + b, 0);
    const sectionWidths = ratio.map(r => (totalW * r) / ratioSum);

    const sectionX1 = sectionWidths[0];
    const sectionX2 = sectionWidths[0] + sectionWidths[1];
    const totalX = totalW;
    const unit = computedUnit.value;

    const positions: { x: number; align: CanvasTextAlign; text: string }[] = [
        { x: 0, align: 'start', text: String(labelList[0]) + unit },
        {
            x: sectionX1,
            align: 'center',
            text: String(labelList[1]) + unit,
        },
        {
            x: sectionX2,
            align: 'center',
            text: String(labelList[2]) + unit,
        },
        { x: totalX, align: 'end', text: String(labelList[3]) + unit },
    ];

    ctx.save();
    ctx.font = '12px sans-serif';
    ctx.textBaseline = 'top';
    const paddingY = 4;
    const labelY = LABEL_TOTAL_HEIGHT + contentHeight + paddingY;

    positions.forEach(({ x, align, text }) => {
        ctx.textAlign = align;
        ctx.fillStyle = '#B3BBC5'; // 기존 UI 기준 회색
        ctx.fillText(text, x, labelY);
    });

    ctx.restore();
};

const drawTrail = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    canvasWidth: number,
    alpha: number,
    isDecrease: boolean,
) => {
    const trailWidth = canvasWidth * 0.5;
    const trailHeight = 5;

    let xStart: number, xEnd: number;
    let gradient: CanvasGradient;

    if (isDecrease) {
        // 🔺 감소 방향: 포인트 오른쪽에 trail (좌 → 우로 흐림)
        xStart = x;
        xEnd = x + trailWidth;

        gradient = ctx.createLinearGradient(xStart, 0, xEnd, 0);
        gradient.addColorStop(0, `rgba(255, 255, 255, ${alpha})`);
        gradient.addColorStop(1, `rgba(255, 255, 255, 0)`);
    } else {
        // 🔺 증가 방향: 포인트 왼쪽에 trail (우 → 좌로 흐림)
        xStart = x - trailWidth;
        xEnd = x;

        gradient = ctx.createLinearGradient(xEnd, 0, xStart, 0); // 우→좌
        gradient.addColorStop(0, `rgba(255, 255, 255, ${alpha})`);
        gradient.addColorStop(1, `rgba(255, 255, 255, 0)`);
    }

    ctx.fillStyle = gradient;
    ctx.fillRect(xStart, y - trailHeight / 2, xEnd - xStart, trailHeight);
};

/** 
// 포인트 그리기 함수
const drawPoint = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number = 5,
) => {
    ctx.beginPath();
    ctx.arc(x, y, size / 2, 0, Math.PI * 2);
    ctx.fillStyle = '#FFFFFF'; // 내부는 흰색
    ctx.fill();
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)'; // 테두리 색상: #000, 알파 30%
    ctx.lineWidth = 1;
    ctx.stroke();
};
*/

const easeOutBack = (t: number): number => {
    const c1 = 0.6;
    const c3 = c1 + 1;

    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

const drawGraphPointWithTrail = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    canvasWidth: number,
    isDecrease: boolean,
    trailAlpha: number,
    isUserPoint: boolean = false,
) => {
    if (trailAlpha > 0) {
        drawTrail(ctx, x, y, canvasWidth, trailAlpha, isDecrease);
    }

    ctx.beginPath();
    ctx.arc(x, y, 2.5, 0, Math.PI * 2); // 👈 반지름 고정
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();
    ctx.strokeStyle = isUserPoint ? '#EE3059' : 'rgba(0, 0, 0, 0.3)';
    ctx.lineWidth = isUserPoint ? USER_LINE_WIDTH : NORMAL_LINE_WIDTH;
    ctx.stroke();
};

// 애니메이션 작동 함수
const animateGraphPoints = () => {
    if (!pointCanvasRef.value || !pointCtx.value || !graphRef.value) return;

    const ctx = pointCtx.value;
    const canvasWidth = effectiveWidth.value;
    const contentHeight = canvasWidth * originRatio;
    const canvasHeight = effectiveWidth.value * originRatio + LABEL_TOTAL_HEIGHT + BOTTOM_LABEL_HEIGHT;

    const offsetY = LABEL_TOTAL_HEIGHT;
    const duration = 1500;

    ctx.save();
    drawFullRoundedClip(ctx, 0, offsetY, canvasWidth, contentHeight, 5);

    const rawPoints = props.data.GraphData ?? [];
    const userPoint = props.data.UserValue;

    const mergedPoints = [...rawPoints];
    if (userPoint) {
        mergedPoints.push(userPoint);
    }

    const userX = userPoint?.X ?? null;
    const userY = userPoint?.Y ?? null;

    const points = mergedPoints.map(({ X, Y, Value }) => {
        const marginX = 0.01;
        const marginY = 0.025;

        let rawX = X ?? 0;
        let rawY = Y ?? 0;

        rawX = Math.max(marginX, Math.min(rawX, 1 - marginX));
        rawY = Math.max(marginY, Math.min(rawY, 1 - marginY));

        const safeX = isDecrease.value ? 1 - rawX : rawX;
        const safeY = rawY;

        const xTarget = safeX * canvasWidth;
        const yTarget = offsetY + safeY * contentHeight;

        let normX = xTarget / canvasWidth;
        normX = Math.max(0, Math.min(normX, 1));
        const [n0, n1, n2, n3] = filteredNormalized.value;

        let trailAlpha = 0;
        if (isDecrease.value) {
            const reversedX = 1 - normX;
            if (reversedX >= n2 && reversedX < n1) trailAlpha = 0.025;
            if (reversedX >= n1 && reversedX <= n0) trailAlpha = 0.3;
        } else {
            if (normX >= n1 && normX < n2) trailAlpha = 0.025;
            if (normX >= n2 && normX <= n3) trailAlpha = 0.3;
        }

        const isUserPoint = X === userX && Y === userY;

        return {
            x: isDecrease.value ? canvasWidth : 0,
            y: yTarget,
            targetX: xTarget,
            targetY: yTarget,
            value: Value,
            delay: Math.random() * 300,
            trailAlpha,
            isUserPoint,
        };
    });

    animatedPoints.value = points;

    const startTime = performance.now();

    const animate = (now: number) => {
        const elapsed = now - startTime;
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);

        for (const p of points) {
            if (elapsed < p.delay) continue;

            const localT = Math.min((elapsed - p.delay) / duration, 1);
            const easedT = easeOutBack(localT);
            const currentX = p.x + (p.targetX - p.x) * easedT;
            const currentY = p.y;

            drawGraphPointWithTrail(
                ctx,
                currentX,
                currentY,
                canvasWidth,
                isDecrease.value,
                p.trailAlpha,
                p.isUserPoint, // ✅ 유저 포인트 강조 적용
            );
        }

        if (elapsed < duration + 300) {
            requestAnimationFrame(animate);
        } else {
            ctx.restore();
            drawStaticPoints();

            const user = points.find(p => p.isUserPoint);
            if (user) {
                startAnimationForUserPoint(user.targetX, user.targetY);
            }
        }
    };

    requestAnimationFrame(animate);
};

const drawStaticPoints = () => {
    if (!pointCtx.value || animatedPoints.value.length === 0) return;
    const ctx = pointCtx.value;

    const canvasWidth = effectiveWidth.value;
    const contentHeight = canvasWidth * originRatio;
    const canvasHeight = canvasWidth * originRatio + LABEL_TOTAL_HEIGHT + BOTTOM_LABEL_HEIGHT;

    const offsetY = LABEL_TOTAL_HEIGHT;

    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // 클리핑 적용
    ctx.save();
    drawFullRoundedClip(ctx, 0, offsetY, canvasWidth, contentHeight, 5);

    for (const { targetX, targetY, trailAlpha, isUserPoint } of animatedPoints.value) {
        drawGraphPointWithTrail(ctx, targetX, targetY, canvasWidth, isDecrease.value, trailAlpha, isUserPoint);
    }

    ctx.restore(); // ✅ 클리핑 해제
};

const startAnimationForUserPoint = (posX: number, posY: number) => {
    const ctx = waveCtx.value;
    if (!ctx) return;

    const canvasWidth = effectiveWidth.value;
    const canvasHeight = canvasWidth * originRatio + LABEL_TOTAL_HEIGHT + BOTTOM_LABEL_HEIGHT;

    // const dpr = 3;
    const arcRadius = 2.5; // 포인트와 동일한 크기
    const lineWidth = USER_LINE_WIDTH; // 예: 2.5
    const duration = 2000;
    let startTime: number | null = null;

    const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const elapsed = (timestamp - startTime) % duration;
        const progress = elapsed / duration;

        ctx.clearRect(0, 0, canvasWidth, canvasHeight);

        const opacity = 0.5 * (progress < 0.5 ? progress / 0.5 : 1 - (progress - 0.5) / 0.5);

        const currentRadius1 = arcRadius * 4.5 * progress;
        const currentRadius2 = arcRadius * 9 * progress;

        ctx.save();

        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = opacity;

        [currentRadius1, currentRadius2].forEach(radius => {
            ctx.beginPath();
            ctx.arc(posX, posY, radius, 0, 2 * Math.PI);
            ctx.fillStyle = 'rgba(255, 255, 255, 1)';
            ctx.fill();
        });

        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';

        ctx.beginPath();
        ctx.arc(posX, posY, arcRadius, 0, 2 * Math.PI);
        ctx.fillStyle = '#FFFFFF';
        ctx.fill();
        ctx.lineWidth = lineWidth;
        ctx.strokeStyle = '#EE3059';
        ctx.stroke();

        drawSpeechBubble(ctx, posX, posY);

        ctx.restore();
        waveAnimationFrameId.value = requestAnimationFrame(animate);
    };

    waveAnimationFrameId.value = requestAnimationFrame(animate);
};

const drawSpeechBubble = (ctx: CanvasRenderingContext2D, targetX: number, targetY: number) => {
    const BALLOON_HEIGHT = 18;
    const TAIL_HEIGHT = 4;
    const PADDING_X = 6;
    const RADIUS = 4;
    const MARGIN_Y = 1;

    const BOLD_FONT = 'bold 12px sans-serif';
    const NORMAL_FONT = 'normal 12px sans-serif';
    const TEXT_COLOR = '#606976';
    const BG_COLOR = '#FFFFFF';

    const canvasWidth = effectiveWidth.value;
    const canvasHeight = effectiveWidth.value * originRatio + LABEL_TOTAL_HEIGHT + BOTTOM_LABEL_HEIGHT;

    const value = String(props.data.UserValue?.Value ?? '');
    const unit = computedUnit.value;

    // 텍스트 너비 측정
    ctx.font = BOLD_FONT;
    const valueWidth = ctx.measureText(value).width;
    ctx.font = NORMAL_FONT;
    const unitWidth = ctx.measureText(unit).width;
    const totalTextWidth = valueWidth + unitWidth;
    const balloonWidth = totalTextWidth + PADDING_X * 2;

    // POINT_RADIUS를 고려한 실제 원의 반지름 계산
    const actualPointRadius = POINT_RADIUS * canvasWidth;
    // 기본 위치 (위쪽)
    let rectX = targetX - balloonWidth / 2;
    let rectY = targetY - actualPointRadius - TAIL_HEIGHT - BALLOON_HEIGHT - MARGIN_Y;
    let isHorizontal = false;
    let isLeftSide = false;

    const RIGHT_LEFT_MARGIN = 4;

    // 좌측으로 벗어남 → 말풍선을 오른쪽에 표시
    if (rectX < 0) {
        rectX = targetX + actualPointRadius + 4 + RIGHT_LEFT_MARGIN;
        rectY = targetY - BALLOON_HEIGHT / 2;
        isHorizontal = true;
    }

    // 우측으로 벗어남 → 말풍선을 왼쪽에 표시
    else if (rectX + balloonWidth > canvasWidth) {
        rectX = targetX - balloonWidth - actualPointRadius - 4 - RIGHT_LEFT_MARGIN;
        rectY = targetY - BALLOON_HEIGHT / 2;
        isHorizontal = true;
        isLeftSide = true;
    }

    // 위 공간 부족하면 아래로 위치
    if (!isHorizontal) {
        const spaceAbove = rectY;
        const spaceBelow = canvasHeight - (targetY + actualPointRadius + TAIL_HEIGHT + BALLOON_HEIGHT + MARGIN_Y);
        const isBelow = spaceAbove < 0 && spaceBelow > 0;

        rectY = isBelow
            ? targetY + actualPointRadius + TAIL_HEIGHT + MARGIN_Y
            : targetY - actualPointRadius - TAIL_HEIGHT - BALLOON_HEIGHT - MARGIN_Y;
    }

    // 🟩 말풍선 본체
    ctx.fillStyle = BG_COLOR;
    ctx.beginPath();
    ctx.moveTo(rectX + RADIUS, rectY);
    ctx.lineTo(rectX + balloonWidth - RADIUS, rectY);
    ctx.quadraticCurveTo(rectX + balloonWidth, rectY, rectX + balloonWidth, rectY + RADIUS);
    ctx.lineTo(rectX + balloonWidth, rectY + BALLOON_HEIGHT - RADIUS);
    ctx.quadraticCurveTo(
        rectX + balloonWidth,
        rectY + BALLOON_HEIGHT,
        rectX + balloonWidth - RADIUS,
        rectY + BALLOON_HEIGHT,
    );
    ctx.lineTo(rectX + RADIUS, rectY + BALLOON_HEIGHT);
    ctx.quadraticCurveTo(rectX, rectY + BALLOON_HEIGHT, rectX, rectY + BALLOON_HEIGHT - RADIUS);
    ctx.lineTo(rectX, rectY + RADIUS);
    ctx.quadraticCurveTo(rectX, rectY, rectX + RADIUS, rectY);
    ctx.closePath();
    ctx.fill();

    // 🔺 꼬리
    ctx.beginPath();
    ctx.fillStyle = BG_COLOR;

    if (isHorizontal) {
        const tailCenterY = rectY + BALLOON_HEIGHT / 2;
        const tailBaseX = isLeftSide ? rectX + balloonWidth : rectX;
        const tailTipX = isLeftSide ? tailBaseX + TAIL_HEIGHT : tailBaseX - TAIL_HEIGHT;

        ctx.moveTo(tailTipX, tailCenterY);
        ctx.lineTo(tailBaseX, tailCenterY - 4);
        ctx.lineTo(tailBaseX, tailCenterY + 4);
    } else {
        const isBalloonAbovePoint = rectY + BALLOON_HEIGHT < targetY;
        const tailBaseY = isBalloonAbovePoint ? rectY + BALLOON_HEIGHT - 0.5 : rectY + 0.5;
        const tailTipY = isBalloonAbovePoint ? tailBaseY + TAIL_HEIGHT : tailBaseY - TAIL_HEIGHT;

        // 원의 가장자리에 맞춰 꼬리 위치 조정
        const adjustedTailTipY = isBalloonAbovePoint
            ? Math.min(tailTipY, targetY - actualPointRadius)
            : Math.max(tailTipY, targetY + actualPointRadius);

        ctx.moveTo(targetX, adjustedTailTipY);
        ctx.lineTo(targetX - 4, tailBaseY);
        ctx.lineTo(targetX + 4, tailBaseY);
    }

    ctx.closePath();
    ctx.fill();

    // 📝 텍스트
    const textY = rectY + BALLOON_HEIGHT / 2;
    const centerX = rectX + balloonWidth / 2;

    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillStyle = TEXT_COLOR;

    ctx.font = BOLD_FONT;
    ctx.fillText(value, centerX - unitWidth / 2, textY);

    ctx.font = NORMAL_FONT;
    ctx.fillText(unit, centerX + valueWidth / 2, textY);
};

const updateCanvas = async () => {
    // 먼저 배경을 그리고 완료될 때까지 대기
    await drawElem();
};

onMounted(async () => {
    if (!imageCanvasRef.value || !pointCanvasRef.value || !waveCanvasRef.value) return;

    imageCtx.value = imageCanvasRef.value.getContext('2d', {
        willReadFrequently: true,
    });
    pointCtx.value = pointCanvasRef.value.getContext('2d', {
        willReadFrequently: true,
    });

    waveCtx.value = waveCanvasRef.value?.getContext('2d', {
        willReadFrequently: true,
    });

    // DPR 초기화 - setSize()에서 이미 처리됨

    setSize();

    // 초기 캔버스 업데이트 (비동기 처리)
    await updateCanvas();
});

onUnmounted(() => {
    if (waveAnimationFrameId.value !== null) {
        cancelAnimationFrame(waveAnimationFrameId.value);
    }
});
</script>

<style scoped lang="scss">
.variance-graph {
    position: relative;
    width: 100%;
    height: auto;
    display: flex;
    justify-content: center;
    align-items: flex-start;
}

.image-canvas,
.animation-canvas,
.wave-canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: auto;
}

.image-canvas {
    z-index: 1;
}
.point-canvas {
    z-index: 2;
}
.wave-canvas {
    z-index: 3;
    pointer-events: none;
}

.animation-canvas {
    pointer-events: none;
}
</style>
